/**
 * components/object/ObjectAssignmentPicker.jsx — Iter 51.
 *
 * Drawer-friendly inline picker. Calls /api/users to list the tenant
 * members and PATCHes /api/objects/{type}/{id}/status with an
 * `assigned_to` payload. Empty selection = unassigned.
 *
 * The picker shows ONLY users with operational roles (no executives /
 * owners by default — they can still appear by typing in the search).
 * Drawing-first: opens as a compact popover inside the inspector, not
 * a separate page.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { User, Loader2, Check, X } from "lucide-react";
import { api } from "@/lib/api";
import { fetchSummary, updateAssignment } from "@/lib/objectIntel";

const ASSIGNABLE_ROLES = new Set([
  "admin", "owner", "project_manager", "superintendent",
  "field_engineer", "safety_manager", "inspector", "foreman",
  "subcontractor",
]);

export default function ObjectAssignmentPicker({ objectType, objectId, onChanged }) {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState(null);
  const boxRef = useRef(null);

  useEffect(() => {
    let cancel = false;
    fetchSummary(objectType, objectId)
      .then((s) => { if (!cancel) setSummary(s); })
      .catch(() => { if (!cancel) setSummary(null); });
    return () => { cancel = true; };
  }, [objectType, objectId]);

  useEffect(() => {
    if (!open) return;
    let cancel = false;
    api.get("/users").then(({ data }) => {
      if (cancel) return;
      const rows = (data?.users || data || [])
        .filter((u) => ASSIGNABLE_ROLES.has(u.role));
      setUsers(rows);
    }).catch(() => { if (!cancel) setUsers([]); });
    return () => { cancel = true; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return users.slice(0, 12);
    return users.filter((u) =>
      (u.name || "").toLowerCase().includes(needle)
      || (u.email || "").toLowerCase().includes(needle)
      || (u.role || "").toLowerCase().includes(needle),
    ).slice(0, 12);
  }, [users, q]);

  const choose = async (userId) => {
    setBusy(true);
    try {
      await updateAssignment(objectType, objectId, userId || "");
      // Refresh summary to render the new name
      const s = await fetchSummary(objectType, objectId);
      setSummary(s);
      setOpen(false);
      onChanged && onChanged();
    } finally {
      setBusy(false);
    }
  };

  const label = summary?.assigned_to_name
    || (summary?.assigned_to ? "Assigned" : "Unassigned");

  return (
    <div className="fop-surface p-3 rounded-md relative" ref={boxRef}
          data-testid="object-assignment-picker">
      <div className="text-xs uppercase tracking-wider font-mono text-[var(--fop-text-3)] mb-2 flex items-center gap-1.5">
        <User size={12} /> Assigned To
      </div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded border border-[var(--fop-border)] hover:border-[var(--fop-text-3)] text-[12px] text-[var(--fop-text)] bg-[var(--fop-surface)]"
        data-testid="assignment-trigger"
        data-assigned-to={summary?.assigned_to || ""}
      >
        <span className="truncate">{label}</span>
        {summary?.assigned_to && (
          <span className="text-[9px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
            CHANGE
          </span>
        )}
      </button>
      {open && (
        <div className="absolute z-20 left-3 right-3 top-full mt-1 bg-[var(--fop-surface)] border border-[var(--fop-border)] rounded shadow-2xl py-1"
              data-testid="assignment-popover">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search…"
            className="w-full text-[11px] px-2 py-1 mb-1 border-b border-[var(--fop-border)] bg-transparent text-[var(--fop-text)] focus:outline-none"
            data-testid="assignment-search"
            autoFocus
          />
          <ul className="max-h-56 overflow-y-auto">
            {summary?.assigned_to && (
              <li>
                <button
                  onClick={() => choose("")}
                  disabled={busy}
                  className="w-full flex items-center gap-2 px-2 py-1.5 text-[11px] text-[var(--fop-danger,var(--st-escalated))] hover:bg-[var(--fop-surface-2)]"
                  data-testid="assignment-unassign"
                >
                  <X size={11} /> Unassign
                </button>
              </li>
            )}
            {filtered.length === 0 && (
              <li className="px-3 py-3 text-[11px] text-[var(--fop-text-3)]">
                No matches.
              </li>
            )}
            {filtered.map((u) => {
              const me = u.id === summary?.assigned_to;
              return (
                <li key={u.id}>
                  <button
                    onClick={() => choose(u.id)}
                    disabled={busy}
                    className={`w-full flex items-center gap-2 px-2 py-1.5 text-left text-[11px] hover:bg-[var(--fop-surface-2)] ${me ? "text-[var(--fop-primary)]" : "text-[var(--fop-text)]"}`}
                    data-testid={`assignment-option-${u.id}`}
                  >
                    {me ? <Check size={11} className="shrink-0" /> : <User size={11} className="shrink-0" />}
                    <span className="flex-1 truncate">{u.name || u.email}</span>
                    <span className="text-[9px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]">
                      {u.role}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
          {busy && (
            <div className="px-3 py-2 text-[11px] text-[var(--fop-text-3)] flex items-center gap-1.5">
              <Loader2 size={11} className="animate-spin" /> Updating…
            </div>
          )}
        </div>
      )}
    </div>
  );
}
